// Local-pack justifications — the snippets Google shows beneath a business
// in the 3-pack ("Their website mentions...", "Provides...", etc).

export interface Justification {
  slug: string;
  name: string;
  example: string;
  description: string;
  /** What triggers Google to show this justification. */
  trigger: string;
  /** How an operator can encourage it. */
  howToEarn: string[];
  /** Which categories most commonly see it. */
  commonFor: string[];
}

export const JUSTIFICATIONS: Justification[] = [
  {
    slug: "review",
    name: "Review justification",
    example: "“The pizza is incredible” — Review",
    description:
      "Google pulls a sentence from a customer review that contains the searcher's keyword. Most common justification type and the strongest social proof.",
    trigger:
      "User's query keyword (or a close semantic match) appears verbatim inside a published Google review of the business.",
    howToEarn: [
      "Encourage customers to mention specific products/services by name in reviews.",
      "Reply to reviews to reinforce keyword associations.",
      "Run a review-acquisition cadence aimed at your top three commercial keywords.",
    ],
    commonFor: ["Restaurants", "Service businesses", "Retail"],
  },
  {
    slug: "website",
    name: "Website justification",
    example: "“Their website mentions wood-fired pizza”",
    description:
      "Google found the query keyword on the business's linked website and surfaces a short snippet to justify the result.",
    trigger:
      "Website linked in the GBP listing contains the query keyword on an indexable page.",
    howToEarn: [
      "Add service / menu pages that match the search intent.",
      "Use keyword-rich H1s and intro paragraphs (without stuffing).",
      "Link those pages from your homepage so Google reaches them quickly.",
    ],
    commonFor: ["Service-area businesses", "Multi-location retail"],
  },
  {
    slug: "post",
    name: "Post justification",
    example: "“Posted: Summer prix-fixe menu now live”",
    description: "Google highlights a recent Google Post that matches the searcher's intent.",
    trigger: "An active (unexpired) Google Post contains the query keyword.",
    howToEarn: [
      "Publish weekly Posts that align with seasonal demand keywords.",
      "Use call-to-action buttons to surface higher in carousel placements.",
      "Add a clear keyword in the first 80 characters of the post body.",
    ],
    commonFor: ["Restaurants", "Hospitality", "Local retail"],
  },
  {
    slug: "menu",
    name: "Menu / Services justification",
    example: "“Provides Detox Facial”",
    description:
      "Google found the keyword inside the structured services list (or menu, for restaurants) on the GBP listing.",
    trigger: "Services or menu items added to GBP contain the query keyword.",
    howToEarn: [
      "Fill out the structured Services section with every service variant you offer.",
      "For restaurants, keep the structured menu up to date with item names matching real search demand.",
      "Add short, keyword-rich descriptions to each service / menu item.",
    ],
    commonFor: ["Health & Beauty", "Professional Services", "Restaurants"],
  },
  {
    slug: "in-stock",
    name: "In-stock justification",
    example: "“In stock: iPhone 17 Pro”",
    description:
      "Google surfaces that a queried product is in stock at the location — uses the Pointy or merchant inventory feed.",
    trigger:
      "Merchant feed (Pointy, Local Inventory Ads, or Merchant Center with local inventory) marks the queried product as available at that branch.",
    howToEarn: [
      "Integrate a local inventory feed.",
      "For independent retailers, install Pointy and keep the catalogue live.",
      "Ensure product names match common search vernacular, not just SKU codes.",
    ],
    commonFor: ["Retail", "Grocery", "Hardware stores"],
  },
  {
    slug: "from-the-owner",
    name: "From-the-owner justification",
    example: "“From the owner: Family-owned since 1972”",
    description:
      "Google shows the merchant's own description (the 'From the business' field) when it contains the query keyword.",
    trigger: "Query keyword appears in the GBP business description.",
    howToEarn: [
      "Use the 750-character business description to weave in primary keywords naturally.",
      "Front-load distinguishing service or trait keywords ('family-owned', 'wood-fired', 'emergency').",
    ],
    commonFor: ["All categories"],
  },
  {
    slug: "social",
    name: "Social-proof justification",
    example: "“Popular for lunch”",
    description:
      "Google highlights popularity, busyness, or trending signals when relevant to the searcher's likely intent.",
    trigger:
      "Aggregate Google data (visit times, review velocity) indicates the venue is notable for the queried time/use.",
    howToEarn: [
      "Encourage check-ins and Maps directions usage.",
      "Drive review velocity in the period you want to dominate (e.g. lunch).",
    ],
    commonFor: ["Restaurants", "Bars", "Entertainment"],
  },
];

export function getJustificationBySlug(slug: string): Justification | undefined {
  return JUSTIFICATIONS.find((j) => j.slug === slug);
}
